import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Lock, FileX, ArrowLeft } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { LoyaltySidebarLayout } from '../components/LoyaltySidebarLayout';
import { BlockRenderer, type CmsBlock } from '../components/blocks/BlockRenderer';
import { useBranding } from '../contexts/BrandingContext';
import { CmsPageContext } from '../contexts/CmsPageContext';
import { CmsLayoutContext, DEFAULT_LAYOUT, type CmsLayoutConfig } from '../contexts/CmsLayoutContext';
import { applySpaceTheme, applyBranding } from '../lib/branding';
import { getTenantSlug } from '../lib/tenant';
import { getToken } from '../lib/auth';

interface CmsPageData {
  id: string;
  slug: string;
  title: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  ogImageUrl?: string | null;
  isLocked?: boolean;
  loyaltyMembersOnly?: boolean;
  spaceTheme?: Record<string, string>;
  spaceLayout?: Record<string, string>;
  blocks: CmsBlock[];
}

export function CmsPage({ overrideSlug }: { overrideSlug?: string }) {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const slug = overrideSlug ?? routeSlug;
  const branding = useBranding();
  const navigate = useNavigate();
  const [page, setPage] = useState<CmsPageData | null>(null);
  const [layoutConfig, setLayoutConfig] = useState<CmsLayoutConfig>(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [layoutType, setLayoutType] = useState<string>('default');

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);

    // Build request headers — include Authorization if we have a token so the
    // server can unlock loyalty-gated content for authenticated members.
    const headers: Record<string, string> = { 'x-tenant-slug': getTenantSlug() };
    if (branding.defaultSpaceId) headers['x-space-id'] = branding.defaultSpaceId;
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`/customer/portal/pages/${encodeURIComponent(slug)}`, { headers })
      .then(async (res) => {
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) throw new Error('Failed to load page');
        const data = (await res.json()) as CmsPageData;
        setPage(data);

        // Apply per-space theme overrides
        if (data.spaceTheme && Object.keys(data.spaceTheme).length > 0) {
          applySpaceTheme(data.spaceTheme, branding);
        }

        // Determine layout type from space theme (drives sidebar vs. default layout)
        setLayoutType(data.spaceTheme?.layoutType ?? 'default');

        // Apply per-space layout config
        if (data.spaceLayout && Object.keys(data.spaceLayout).length > 0) {
          setLayoutConfig({ ...DEFAULT_LAYOUT, ...data.spaceLayout } as CmsLayoutConfig);
        } else {
          setLayoutConfig(DEFAULT_LAYOUT);
        }

        // Update document title and meta tags
        const title = data.metaTitle ?? data.title;
        document.title = `${title} — ${branding.name}`;

        const metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        if (metaDesc && data.metaDescription) {
          metaDesc.content = data.metaDescription;
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    // Restore portal branding on unmount
    return () => {
      document.title = branding.name;
      applyBranding(branding);
    };
  }, [slug, branding]);

  if (notFound) {
    return (
      <CmsLayoutContext.Provider value={layoutConfig}>
        <AppLayout>
          <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
            <FileX size={40} className="text-zinc-300" />
            <div>
              <h1 className="text-xl font-semibold text-zinc-800">Page not found</h1>
              <p className="text-sm text-zinc-500 mt-1">
                The page you&apos;re looking for doesn&apos;t exist or has been removed.
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors"
            >
              <ArrowLeft size={14} />
              Back to home
            </button>
          </div>
        </AppLayout>
      </CmsLayoutContext.Provider>
    );
  }

  // ── Loyalty sidebar layout ─────────────────────────────────────────────────
  if (layoutType === 'loyalty-sidebar') {
    return (
      <LoyaltySidebarLayout>
        {loading ? (
          <div className="flex items-center justify-center min-h-screen">
            <Loader2 size={24} className="animate-spin text-[#00bc7d]" />
          </div>
        ) : page ? (
          <CmsPageContext.Provider value={{ pageSlug: page.slug, spaceId: branding.defaultSpaceId }}>
            {page.blocks
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((block) => (
                <BlockRenderer key={block.id} block={block} />
              ))}
            {page.blocks.length === 0 && (
              <div className="flex items-center justify-center min-h-[60vh] text-[#62748e] text-sm">
                Questa pagina non ha ancora contenuto.
              </div>
            )}
          </CmsPageContext.Provider>
        ) : null}
      </LoyaltySidebarLayout>
    );
  }

  // ── Default (top-header) layout ────────────────────────────────────────────
  return (
    <CmsLayoutContext.Provider value={layoutConfig}>
      <AppLayout>
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 size={24} className="animate-spin text-zinc-400" />
          </div>
        ) : page ? (
          <CmsPageContext.Provider value={{ pageSlug: page.slug, spaceId: branding.defaultSpaceId }}>
            <div className="space-y-6">
              {/* Page header (shown even for locked pages) */}
              {page.loyaltyMembersOnly && page.isLocked && (
                <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                  <Lock size={14} className="flex-shrink-0" />
                  <span>
                    This page is exclusive to loyalty members.{' '}
                    <button
                      onClick={() => navigate('/login')}
                      className="font-semibold underline hover:no-underline"
                    >
                      Sign in
                    </button>{' '}
                    or{' '}
                    <button
                      onClick={() => navigate('/signup')}
                      className="font-semibold underline hover:no-underline"
                    >
                      create an account
                    </button>{' '}
                    to access the full content.
                  </span>
                </div>
              )}

              {page.blocks
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((block) => (
                  <div key={block.id}>
                    <BlockRenderer block={block} />
                  </div>
                ))}
              {page.blocks.length === 0 && (
                <div className="text-center py-24 text-zinc-400 text-sm">
                  This page has no content yet.
                </div>
              )}
            </div>
          </CmsPageContext.Provider>
        ) : null}
      </AppLayout>
    </CmsLayoutContext.Provider>
  );
}
